import { useCallback, useEffect, useRef } from 'react';
import { appEvents, APP_EVENTS } from '../services/appEvents';

export const useAppDataRefresh = (refreshCallback, dependencies = [], debounceMs = 300) => {
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const lastEventDataRef = useRef(null);

  const debouncedRefresh = useCallback((data) => {
    // Сохраняем последние данные события для повторного обновления
    lastEventDataRef.current = data;
    
    // Очищаем предыдущий таймаут
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(async () => {
      // Если уже идет обновление, помечаем что нужно повторное
      if (isRefreshingRef.current) {
        console.log('Обновление уже выполняется, запланировано повторное...');
        pendingRefreshRef.current = true;
        return;
      }

      try {
        isRefreshingRef.current = true;
        console.log('🔄 Запуск обновления данных...', lastEventDataRef.current);
        await refreshCallback(lastEventDataRef.current);
        
        // Если во время обновления поступил новый запрос, выполняем повторное обновление
        if (pendingRefreshRef.current) {
          console.log('🔄 Выполняем запланированное обновление...');
          pendingRefreshRef.current = false;
          // Рекурсивный вызов для обработки накопленных изменений
          debouncedRefresh(lastEventDataRef.current);
        }
      } catch (error) {
        console.error('❌ Ошибка обновления данных:', error);
        pendingRefreshRef.current = false;
      } finally {
        isRefreshingRef.current = false;
        refreshTimeoutRef.current = null;
      }
    }, debounceMs);
  }, [refreshCallback, debounceMs]);

  useEffect(() => {
    // Подписываемся на все события изменений данных
    const events = [
      APP_EVENTS.DATA_CHANGED,
      APP_EVENTS.TRAINING_COMPLETED,
      APP_EVENTS.TRAINING_ASSIGNED,
      APP_EVENTS.ENROLLMENT_CREATED,
      APP_EVENTS.ENROLLMENT_CANCELLED,
      APP_EVENTS.COURSE_CREATED,
      APP_EVENTS.COURSE_UPDATED,
      APP_EVENTS.COURSE_DELETED,
      APP_EVENTS.EMPLOYEE_CREATED,
      APP_EVENTS.EMPLOYEE_UPDATED,
      APP_EVENTS.EMPLOYEE_DELETED,
      APP_EVENTS.MATRIX_UPDATED,
      APP_EVENTS.EVENT_CREATED,
      APP_EVENTS.EVENT_UPDATED,
      APP_EVENTS.EVENT_DELETED,
      APP_EVENTS.CACHE_INVALIDATED,
    ];

    const unsubscribers = events.map(event => 
      appEvents.on(event, debouncedRefresh)
    );

    return () => {
      unsubscribers.forEach(unsub => unsub());
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [debouncedRefresh, ...dependencies]);

  // Возвращаем функцию для ручного принудительного обновления
  const forceRefresh = useCallback(async () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    if (isRefreshingRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    try {
      isRefreshingRef.current = true;
      await refreshCallback(null);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshCallback]);

  return { forceRefresh, isRefreshing: isRefreshingRef.current };
};